import type { SerializeXlsxOptions } from '../ooxml/serializeWorkbook';
import { serializeXlsxWorkbook } from '../ooxml/serializeWorkbook';
import { preserveOriginalPackageParts } from '../ooxml/preservePackageParts';
import {
  renameSheetReferencesInFormula,
  rewriteFormulaRefsForSheetShift,
} from '../algorithms/structuralShift';
import { mergeXlsxEditSnapshot } from '../model/mergeEditSnapshot';
import type { XlsxEditSnapshot } from '../model/editSnapshot';
import type { XlsxSheet, XlsxWorkbookModel } from '../model/types';

export interface XlsxSessionState {
  revision: number;
  savedRevision: number;
  isDirty: boolean;
  canUndo: boolean;
  canRedo: boolean;
  activeSheetIndex: number;
  sheetCount: number;
}

export interface XlsxSessionSnapshot extends XlsxSessionState {
  model: XlsxWorkbookModel;
}

/** Immutable save/download boundary captured from one exact workbook revision. */
export interface XlsxSessionCapture {
  revision: number;
  workbook: Readonly<XlsxWorkbookModel>;
}

export type XlsxSessionCommand =
  | { type: 'apply-sheet-snapshot'; snapshot: XlsxEditSnapshot }
  | { type: 'replace-sheet'; sheet: XlsxSheet; activate?: boolean }
  | { type: 'insert-sheet'; name: string; index?: number; activate?: boolean }
  | { type: 'delete-sheet'; sheetName: string }
  | { type: 'rename-sheet'; sheetName: string; nextName: string }
  | { type: 'copy-sheet'; sheetName: string; nextName: string; index?: number; activate?: boolean }
  | { type: 'move-sheet'; sheetName: string; toIndex: number }
  | { type: 'set-active-sheet'; sheetName: string };

export interface XlsxWorkbookSessionOptions {
  /** Full-workbook history frames retained by the engine. Defaults to 50. */
  maxHistory?: number;
}

export interface XlsxSessionSerializeOptions extends SerializeXlsxOptions {
  /** Optional original package sidecar for images/charts/embeddings unsupported by SheetJS. */
  preserveOriginalPackage?: {
    buffer: ArrayBuffer;
    sheetOriginalIndices: ReadonlyMap<string, number>;
  };
}

export interface ReplaceXlsxSessionModelOptions {
  /** Keep the previous model as one undo frame. Defaults to false for external loads. */
  recordHistory?: boolean;
  /** Treat the replacement as the persisted checkpoint. Defaults to false. */
  markSaved?: boolean;
}

export type XlsxSessionListener = (state: XlsxSessionState) => void;

interface HistoryFrame {
  model: XlsxWorkbookModel;
  revision: number;
}

type HistoryMode = 'record' | 'preserve' | 'reset';

const cloneJson = <T>(value: T): T => JSON.parse(JSON.stringify(value)) as T;

const deepFreeze = <T>(value: T): T => {
  if (value && typeof value === 'object' && !Object.isFrozen(value)) {
    Object.freeze(value);
    for (const child of Object.values(value as Record<string, unknown>)) {
      deepFreeze(child);
    }
  }
  return value;
};

const INVALID_SHEET_NAME = /[\\/?:*\[\]]/;

const normalizeSheetName = (input: string): string => {
  const name = input.trim();
  if (!name) throw new Error('XLSX sheet name must not be empty.');
  if (name.length > 31) throw new Error(`XLSX sheet name must not exceed 31 characters: ${name}`);
  if (INVALID_SHEET_NAME.test(name)) throw new Error(`Invalid XLSX sheet name: ${name}`);
  return name;
};

const sheetNameKey = (name: string): string => name.toLocaleLowerCase('en-US');

export const cloneXlsxWorkbookModel = (model: XlsxWorkbookModel): XlsxWorkbookModel =>
  cloneJson(model);

export const createEmptyXlsxSheet = (name: string): XlsxSheet => ({
  name: normalizeSheetName(name),
  rowCount: 50,
  colCount: 26,
  cells: {},
  merges: [],
});

const normalizeWorkbook = (input: XlsxWorkbookModel): XlsxWorkbookModel => {
  const model = cloneXlsxWorkbookModel(input);
  if (model.sheets.length === 0) {
    model.sheets.push(createEmptyXlsxSheet('Sheet1'));
  }
  const names = new Set<string>();
  for (const sheet of model.sheets) {
    const name = normalizeSheetName(sheet.name);
    const key = sheetNameKey(name);
    if (names.has(key)) throw new Error(`Duplicate XLSX sheet name: ${name}`);
    names.add(key);
    sheet.name = name;
    sheet.rowCount = Math.max(1, Math.trunc(sheet.rowCount || 1));
    sheet.colCount = Math.max(1, Math.trunc(sheet.colCount || 1));
  }
  const requested = Number.isFinite(model.activeSheetIndex)
    ? Math.trunc(model.activeSheetIndex as number)
    : 0;
  model.activeSheetIndex = Math.min(Math.max(requested, 0), model.sheets.length - 1);
  return model;
};

const clampInsertIndex = (index: number | undefined, length: number): number => {
  if (!Number.isFinite(index)) return length;
  return Math.min(Math.max(Math.trunc(index as number), 0), length);
};

const sameModel = (left: XlsxWorkbookModel, right: XlsxWorkbookModel): boolean =>
  JSON.stringify(left) === JSON.stringify(right);

const rewriteWorkbookSheetReferences = (
  model: XlsxWorkbookModel,
  oldSheetName: string,
  nextSheetName: string,
): void => {
  const rewrite = (formula: string): string =>
    renameSheetReferencesInFormula(formula, oldSheetName, nextSheetName);

  for (const sheet of model.sheets) {
    for (const cell of Object.values(sheet.cells)) {
      if (typeof cell.formula === 'string') cell.formula = rewrite(cell.formula);
    }
    for (const validation of sheet.dataValidations ?? []) {
      if (typeof validation.formula1 === 'string') validation.formula1 = rewrite(validation.formula1);
      if (typeof validation.formula2 === 'string') validation.formula2 = rewrite(validation.formula2);
    }
    for (const conditionalFormat of sheet.conditionalFormats ?? []) {
      for (const field of ['formula', 'formulas'] as const) {
        const formulas = conditionalFormat.rule[field];
        if (typeof formulas === 'string') {
          conditionalFormat.rule[field] = rewrite(formulas);
        } else if (Array.isArray(formulas)) {
          conditionalFormat.rule[field] = formulas.map((formula) =>
            typeof formula === 'string' ? rewrite(formula) : formula,
          );
        }
      }
    }
  }
};

/**
 * A structural snapshot already contains the fully shifted target sheet. Only
 * formulas owned by the other sheets are rewritten here, so the snapshot can
 * be merged and cross-sheet references updated in one session history frame.
 */
const rewriteOtherSheetStructuralReferences = (
  model: XlsxWorkbookModel,
  snapshot: XlsxEditSnapshot,
): void => {
  for (const shift of snapshot.structuralShifts ?? []) {
    if (
      shift.sheetName !== snapshot.sheet.name
      || (shift.axis !== 'row' && shift.axis !== 'col')
      || !Number.isInteger(shift.pivot)
      || shift.pivot < 0
      || !Number.isInteger(shift.delta)
      || shift.delta === 0
    ) continue;

    for (const sheet of model.sheets) {
      if (sheet.name === snapshot.sheet.name) continue;
      const rewrite = (formula: string): string => rewriteFormulaRefsForSheetShift(
        formula,
        sheet.name,
        shift.sheetName,
        shift.axis,
        shift.pivot,
        shift.delta,
      );
      for (const cell of Object.values(sheet.cells)) {
        if (typeof cell.formula === 'string') cell.formula = rewrite(cell.formula);
      }
      for (const validation of sheet.dataValidations ?? []) {
        if (typeof validation.formula1 === 'string') validation.formula1 = rewrite(validation.formula1);
        if (typeof validation.formula2 === 'string') validation.formula2 = rewrite(validation.formula2);
      }
      for (const conditionalFormat of sheet.conditionalFormats ?? []) {
        for (const field of ['formula', 'formulas'] as const) {
          const value = conditionalFormat.rule[field];
          if (typeof value === 'string') {
            conditionalFormat.rule[field] = rewrite(value);
          } else if (Array.isArray(value)) {
            conditionalFormat.rule[field] = value.map((formula) =>
              typeof formula === 'string' ? rewrite(formula) : formula);
          }
        }
      }
    }
  }
};

/**
 * Host-neutral full-workbook edit session.
 *
 * React selection/gesture state deliberately stays outside this class. The session
 * owns the canonical workbook, persisted checkpoint, document-level history, sheet
 * structure commands, and the engine serialization path.
 */
export class XlsxWorkbookSession {
  private model: XlsxWorkbookModel;
  private revision = 0;
  private savedRevision = 0;
  private nextRevision = 1;
  /**
   * `replaceModel(..., { recordHistory: false })` starts a new external-load
   * lineage. A late save acknowledgement from the previous lineage must not
   * move the new workbook's persisted checkpoint backwards.
   */
  private generation = 0;
  private readonly revisionGeneration = new Map<number, number>([[0, 0]]);
  private readonly maxHistory: number;
  private readonly undoStack: HistoryFrame[] = [];
  private readonly redoStack: HistoryFrame[] = [];
  private readonly listeners = new Set<XlsxSessionListener>();

  constructor(model: XlsxWorkbookModel, options: XlsxWorkbookSessionOptions = {}) {
    this.model = normalizeWorkbook(model);
    this.maxHistory = Math.max(0, Math.trunc(options.maxHistory ?? 50));
  }

  getState(): XlsxSessionState {
    return {
      revision: this.revision,
      savedRevision: this.savedRevision,
      isDirty: this.revision !== this.savedRevision,
      canUndo: this.undoStack.length > 0,
      canRedo: this.redoStack.length > 0,
      activeSheetIndex: this.model.activeSheetIndex ?? 0,
      sheetCount: this.model.sheets.length,
    };
  }

  getModel(): XlsxWorkbookModel {
    return cloneXlsxWorkbookModel(this.model);
  }

  getSheet(nameOrIndex: string | number): XlsxSheet | null {
    const sheet = typeof nameOrIndex === 'number'
      ? this.model.sheets[nameOrIndex]
      : this.model.sheets.find((entry) => entry.name === nameOrIndex);
    return sheet ? cloneJson(sheet) : null;
  }

  getSnapshot(): XlsxSessionSnapshot {
    return { ...this.getState(), model: this.getModel() };
  }

  capture(): XlsxSessionCapture {
    const workbook = deepFreeze(this.getModel());
    return Object.freeze({ revision: this.revision, workbook });
  }

  subscribe(listener: XlsxSessionListener): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  /**
   * Switch the visible sheet without creating a document revision.
   *
   * Hosts should use this for tab navigation: the active index remains part of
   * the model (and subscribers are notified), while save checkpoints and
   * document undo/redo history stay exactly as they were.
   */
  setActiveSheetView(sheetName: string): boolean {
    const index = this.model.sheets.findIndex((sheet) => sheet.name === sheetName);
    if (index < 0 || index === (this.model.activeSheetIndex ?? 0)) return false;
    this.model = { ...this.model, activeSheetIndex: index };
    this.notify();
    return true;
  }

  replaceModel(
    model: XlsxWorkbookModel,
    options: ReplaceXlsxSessionModelOptions = {},
  ): boolean {
    const next = normalizeWorkbook(model);
    if (sameModel(this.model, next)) {
      if (options.markSaved) {
        this.savedRevision = this.revision;
        this.notify();
      }
      return false;
    }
    this.commit(next, options.recordHistory ? 'record' : 'reset');
    if (options.markSaved) this.savedRevision = this.revision;
    this.notify();
    return true;
  }

  dispatch(command: XlsxSessionCommand | readonly XlsxSessionCommand[]): boolean {
    const commands = Array.isArray(command) ? command : [command];
    if (commands.length === 0) return false;
    let next = this.model;
    for (const item of commands) next = this.reduce(next, item);
    if (next === this.model || sameModel(this.model, next)) return false;
    // Sheet navigation is persisted but is not a document edit and therefore
    // must not become the next Ctrl+Z target. A mixed command batch is still a
    // single document transaction and receives exactly one history frame.
    const historyMode: HistoryMode = commands.some((item) => item.type !== 'set-active-sheet')
      ? 'record'
      : 'preserve';
    this.commit(next, historyMode);
    this.notify();
    return true;
  }

  transaction(
    update:
      | readonly XlsxSessionCommand[]
      | ((model: XlsxWorkbookModel) => XlsxWorkbookModel),
  ): boolean {
    if (typeof update !== 'function') return this.dispatch(update);
    const next = normalizeWorkbook(update(this.getModel()));
    if (sameModel(this.model, next)) return false;
    this.commit(next, 'record');
    this.notify();
    return true;
  }

  undo(): boolean {
    const previous = this.undoStack.pop();
    if (!previous) return false;
    this.redoStack.push({ model: this.model, revision: this.revision });
    this.model = previous.model;
    this.revision = previous.revision;
    this.notify();
    return true;
  }

  redo(): boolean {
    const next = this.redoStack.pop();
    if (!next) return false;
    this.pushUndo({ model: this.model, revision: this.revision });
    this.model = next.model;
    this.revision = next.revision;
    this.notify();
    return true;
  }

  markSaved(revision = this.revision): boolean {
    if (!Number.isInteger(revision)) return false;
    if (this.revisionGeneration.get(revision) !== this.generation) return false;
    if (this.savedRevision === revision) return false;
    this.savedRevision = revision;
    this.notify();
    return true;
  }

  async serialize(options?: XlsxSessionSerializeOptions): Promise<ArrayBuffer>;
  async serialize(
    capture: XlsxSessionCapture,
    options?: XlsxSessionSerializeOptions,
  ): Promise<ArrayBuffer>;
  async serialize(
    captureOrOptions: XlsxSessionCapture | XlsxSessionSerializeOptions = {},
    maybeOptions: XlsxSessionSerializeOptions = {},
  ): Promise<ArrayBuffer> {
    const isCapture = 'workbook' in captureOrOptions && 'revision' in captureOrOptions;
    const workbook = isCapture
      ? captureOrOptions.workbook as XlsxWorkbookModel
      : this.model;
    const options = isCapture ? maybeOptions : captureOrOptions;
    const { preserveOriginalPackage, ...writerOptions } = options;
    const outputBuffer = await serializeXlsxWorkbook(workbook, writerOptions);
    if (!preserveOriginalPackage) return outputBuffer;
    return preserveOriginalPackageParts({
      outputBuffer,
      originalBuffer: preserveOriginalPackage.buffer,
      sheetOriginalIndices: preserveOriginalPackage.sheetOriginalIndices,
      outputSheetNames: workbook.sheets.map((sheet) => sheet.name),
    });
  }

  private commit(next: XlsxWorkbookModel, historyMode: HistoryMode): void {
    if (historyMode === 'record') {
      this.pushUndo({ model: this.model, revision: this.revision });
      this.redoStack.length = 0;
    } else if (historyMode === 'reset') {
      this.undoStack.length = 0;
      this.redoStack.length = 0;
      this.generation += 1;
      this.revisionGeneration.clear();
    }
    this.model = next;
    this.revision = this.nextRevision++;
    this.revisionGeneration.set(this.revision, this.generation);
  }

  private pushUndo(frame: HistoryFrame): void {
    if (this.maxHistory === 0) return;
    this.undoStack.push(frame);
    while (this.undoStack.length > this.maxHistory) this.undoStack.shift();
  }

  private notify(): void {
    const state = this.getState();
    this.listeners.forEach((listener) => listener(state));
  }

  private reduce(current: XlsxWorkbookModel, command: XlsxSessionCommand): XlsxWorkbookModel {
    switch (command.type) {
      case 'apply-sheet-snapshot':
      {
        const model = normalizeWorkbook(mergeXlsxEditSnapshot(command.snapshot, current));
        rewriteOtherSheetStructuralReferences(model, command.snapshot);
        // The sheet name is the stable identity. Prefer it over a possibly stale
        // numeric index captured before a concurrent insert/move operation.
        const activeIndex = model.sheets.findIndex(
          (sheet) => sheet.name === command.snapshot.sheet.name,
        );
        if (activeIndex >= 0) model.activeSheetIndex = activeIndex;
        return model;
      }

      case 'replace-sheet': {
        const model = cloneXlsxWorkbookModel(current);
        const index = model.sheets.findIndex((sheet) => sheet.name === command.sheet.name);
        const sheet = cloneJson(command.sheet);
        if (index >= 0) model.sheets[index] = sheet;
        else model.sheets.push(sheet);
        if (command.activate) model.activeSheetIndex = index >= 0 ? index : model.sheets.length - 1;
        return normalizeWorkbook(model);
      }

      case 'insert-sheet': {
        const name = normalizeSheetName(command.name);
        if (current.sheets.some((sheet) => sheetNameKey(sheet.name) === sheetNameKey(name))) {
          throw new Error(`Duplicate XLSX sheet name: ${name}`);
        }
        const model = cloneXlsxWorkbookModel(current);
        const index = clampInsertIndex(command.index, model.sheets.length);
        const activeName = model.sheets[model.activeSheetIndex ?? 0]?.name;
        model.sheets.splice(index, 0, createEmptyXlsxSheet(name));
        model.activeSheetIndex = command.activate
          ? index
          : Math.max(0, model.sheets.findIndex((sheet) => sheet.name === activeName));
        return normalizeWorkbook(model);
      }

      case 'delete-sheet': {
        if (current.sheets.length <= 1) return current;
        const index = current.sheets.findIndex((sheet) => sheet.name === command.sheetName);
        if (index < 0) return current;
        const model = cloneXlsxWorkbookModel(current);
        const activeName = model.sheets[model.activeSheetIndex ?? 0]?.name;
        model.sheets.splice(index, 1);
        const survivingActive = model.sheets.findIndex((sheet) => sheet.name === activeName);
        model.activeSheetIndex = survivingActive >= 0
          ? survivingActive
          : Math.min(index, model.sheets.length - 1);
        return normalizeWorkbook(model);
      }

      case 'rename-sheet': {
        const index = current.sheets.findIndex((sheet) => sheet.name === command.sheetName);
        if (index < 0) return current;
        const nextName = normalizeSheetName(command.nextName);
        if (nextName === command.sheetName) return current;
        if (current.sheets.some(
          (sheet, currentIndex) =>
            currentIndex !== index && sheetNameKey(sheet.name) === sheetNameKey(nextName),
        )) {
          throw new Error(`Duplicate XLSX sheet name: ${nextName}`);
        }
        const model = cloneXlsxWorkbookModel(current);
        model.sheets[index].name = nextName;
        rewriteWorkbookSheetReferences(model, command.sheetName, nextName);
        return normalizeWorkbook(model);
      }

      case 'copy-sheet': {
        const sourceIndex = current.sheets.findIndex((sheet) => sheet.name === command.sheetName);
        if (sourceIndex < 0) return current;
        const nextName = normalizeSheetName(command.nextName);
        if (current.sheets.some((sheet) => sheetNameKey(sheet.name) === sheetNameKey(nextName))) {
          throw new Error(`Duplicate XLSX sheet name: ${nextName}`);
        }
        const model = cloneXlsxWorkbookModel(current);
        const copy = cloneJson(model.sheets[sourceIndex]);
        copy.name = nextName;
        const index = clampInsertIndex(command.index ?? sourceIndex + 1, model.sheets.length);
        const activeName = model.sheets[model.activeSheetIndex ?? 0]?.name;
        model.sheets.splice(index, 0, copy);
        model.activeSheetIndex = command.activate
          ? index
          : Math.max(0, model.sheets.findIndex((sheet) => sheet.name === activeName));
        return normalizeWorkbook(model);
      }

      case 'move-sheet': {
        const from = current.sheets.findIndex((sheet) => sheet.name === command.sheetName);
        if (from < 0) return current;
        const to = Math.min(Math.max(Math.trunc(command.toIndex), 0), current.sheets.length - 1);
        if (from === to) return current;
        const model = cloneXlsxWorkbookModel(current);
        const activeName = model.sheets[model.activeSheetIndex ?? 0]?.name;
        const [sheet] = model.sheets.splice(from, 1);
        model.sheets.splice(to, 0, sheet);
        model.activeSheetIndex = Math.max(
          0,
          model.sheets.findIndex((entry) => entry.name === activeName),
        );
        return normalizeWorkbook(model);
      }

      case 'set-active-sheet': {
        const index = current.sheets.findIndex((sheet) => sheet.name === command.sheetName);
        if (index < 0 || index === (current.activeSheetIndex ?? 0)) return current;
        const model = cloneXlsxWorkbookModel(current);
        model.activeSheetIndex = index;
        return model;
      }
    }
  }
}

export const createXlsxWorkbookSession = (
  model: XlsxWorkbookModel,
  options?: XlsxWorkbookSessionOptions,
): XlsxWorkbookSession => new XlsxWorkbookSession(model, options);
